/**
 * Dependencies
 */
import http from '../../../../services/HttpService'

/**
 * Set the user subscription object
 *
 * @param  {Function} options.commit
 * @param  {Object} subscription
 * @return {Void}
 */
const setSubscription = ({ commit }, subscription) => {
    commit('SET_SUBSCRIPTION', subscription)
}

/**
 * Get the available plans
 *
 * @param  {Function} options.commit
 * @return {Void}
 */
const getPlans = ({ commit }) => {
    http
        .get('information/plans')
        .then(res => commit('SET_PLANS', res.data))
        .catch(err => console.log(err))
}

/**
 * Get the licenses associated with a subscription
 *
 * @param  {Function} options.commit
 * @param  {Object} options.rootState
 * @return {Void}
 */
const getLicenses = ({ commit, rootState }) => {
    http
        .get(`accounts/companies/${rootState.company.data.id}/licenses/`)
        .then(res => commit('SET_LICENSES', res.data.data))
        .catch(err => console.log(err))
}

/**
 * Get the update options for a given user's plan
 *
 * @param  {Function} options.commit
 * @param  {Object} options.rootState
 * @return {Promise}
 */
const getPlanUpdateOptions = ({ commit, rootState }) => {
    return http
        .get(`accounts/companies/${rootState.company.data.id}/subscription-update`)
        .then(res => commit('SET_UPDATE_OPTIONS', res.data))
        .catch(err => err.response)
}

/**
 * Get the subscription details
 *
 * @param  {Function} options.commit
 * @param  {Object} options.rootState
 * @return {Promise}
 */
const getSubscriptionDetails = ({ commit, rootState }) => {
    return http
        .get(`accounts/companies/${rootState.company.data.id}/subscription`)
        .then(res => commit('SET_SUBSCRIPTION', res.data))
        .catch(err => err.response)
}

/**
 * Update the payment method
 *
 * @param  {Function} options.commit
 * @param  {Object} options.rootState
 * @param  {Function} options.dispatch
 * @param  {String} token
 * @return {Promise}
 */
const updatePaymentMethod = ({ commit, rootState, dispatch }, token) => {
    let url = `accounts/companies/${rootState.company.data.id}/subscription/payment/`
    return new Promise((resolve, reject) => {
        return http
            .patch(url, { payment_token: token })
            .then(res => {
                dispatch('getSubscriptionDetails')
                return resolve(res)
            })
            .catch(err => reject(err))
    })
}

/**
 * Update an existing subscription
 *
 * @param  {Function} options.commit
 * @param  {Object} options.rootState
 * @param  {Object} plan
 * @return {Void}
 */
const updateSubscription = ({ commit, rootState }, plan) => {
    let url = `accounts/companies/${rootState.company.data.id}/subscription/`

    return http.patch(url, { lucro_plan: plan })
}

/**
 * Attempt to upgrade an account from a basic account
 *
 * @param  {Function} options.commit
 * @param  {Object} options.rootState
 * @param  {Object} data
 * @return {Promise}
 */
const upgradeSubscriptionFromBasic = ({ commit, rootState, dispatch }, data) => {
    let url = `accounts/companies/${rootState.company.data.id}/subscription/`
    return http
        .patch(url, data)
        .then(res => commit('UPGRADE_SUCCESS', res.data))
        .catch(err => commit('UPGRADE_FAILURE', err))
}

/**
 * Add new licenses to an existing subscription
 *
 * @param  {Function} options.commit
 * @param  {Object} payload
 * @return {Promise}
 */
const addLicenses = ({ commit, rootState, dispatch, state }, emails) => {
    let url = `accounts/companies/${rootState.company.data.id}/licenses/`
    return new Promise((resolve, reject) => {
        return http
            .post(url, {
                num_licenses: state.preview.licenses,
                emails: emails
            })
            .then(res => {
                dispatch('getSubscriptionDetails')
                dispatch('getLicenses')
                dispatch('getPlanUpdateOptions')
                return resolve(res)
            })
            .catch(err => reject(err))
    })
}

/**
 * Remove an existing license
 *
 * @param  {Function} options.commit
 * @param  {Object} options.rootState
 * @param  {Object} payload
 * @return {Promise}
 */
const removeLicense = ({ commit, rootState }, payload) => {
    let emails = payload.emails
    let count = payload.license_count
    let id = rootState.company.data.id
    return http
        .del(`accounts/companies/${id}/licenses/?num_licenses=${count}&emails=${emails}`)
}

/**
 * Set the additional licenses the user wants to previes
 *
 * @param  {Number} value
 * @return {Void}
 */
const setAdditionalLicenses = ({ commit }, value) => commit('UPDATE_PREVIEW_LICENSES', value)

/**
 * Get the preview data for the additional license request
 *
 * @param  {Function} options.commit
 * @param  {Object} options.rootState
 * @return {Promise}
 */
const getPreview = ({ commit, rootState, state }) => {
    const url = `accounts/companies/${rootState.company.data.id}/licenses/preview/?num_licenses=${state.preview.licenses}`
    commit('RESET_PREVIEW_ERROR')
    commit('SET_PREVIEW_LOADING')
    http.get(url)
        .then(res => commit('SET_PREVIEW_DATA', res.data))
        .catch(() => commit('SET_PREVIEW_ERROR'))
}

/**
 * Reset the preview state
 *
 * @param  {Function} options.commit
 * @return {Promise}
 */
const resetPreview = ({ commit }) => commit('RESET_PREVIEW_DATA')

/**
 * Make request to copy a project and start a free trial
 *
 * @param  {Function} options.commit
 * @param  {Object} payload
 * @return {Promise}
 */
const startFreeTrial = ({ commit }, payload) => {
    const url = `accounts/guest-user-signup/?project_id=${payload.id}`
    return new Promise((resolve, reject) => {
        http.post(url, payload.form)
            .then(res => {
                resolve(res.data)
            })
            .catch(err => {
                reject(err)
            })
    })
}

export default {
    getPlans,
    getPreview,
    addLicenses,
    getLicenses,
    resetPreview,
    removeLicense,
    startFreeTrial,
    setSubscription,
    updateSubscription,
    updatePaymentMethod,
    getPlanUpdateOptions,
    setAdditionalLicenses,
    getSubscriptionDetails,
    upgradeSubscriptionFromBasic
}



// WEBPACK FOOTER //
// ./src/state/modules/user/subscription/actions.js